import { useCallback, useEffect, useRef, useState } from "react";
import { Download, FileSpreadsheet, UploadCloud, AlertTriangle, Loader2, FileDown, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, download, errMsg, fmtDate, fmtNum, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusChip } from "@/components/common";
import DataTable from "@/components/DataTable";
import { cn } from "@/lib/utils";
import { SapCodesPreview } from "@/components/SapCodesPreview";

function UploadDialog({ type, onClose, onStarted }) {
  const [file, setFile] = useState(null);
  const [plant, setPlant] = useState("");
  const [plants, setPlants] = useState([]);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [sent, setSent] = useState(null);
  const input = useRef();
  useEffect(() => { setFile(null); setErr(null); setSent(null); setPlant(type?.default_plant || ""); if (type?.needs_plant) api.get("/plants").then((r) => setPlants(r.data.items || r.data)); }, [type]);
  const chunked = async () => {
    const { data: c } = await api.post("/uploads/chunked/start", { filename: file.name, size: file.size });
    for (let n = 0; n < c.parts; n++) {
      const part = file.slice(n * c.chunk_size, Math.min(file.size, (n + 1) * c.chunk_size));
      for (let a = 1; ; a++) {
        try { await api.put(`/uploads/chunked/${c.token}/${n}`, part, { headers: { "Content-Type": "application/octet-stream" }, timeout: 120000 }); break; } catch (e) { if (a >= 4 || e.response?.status < 500) throw e; await new Promise((r) => setTimeout(r, 1500 * a)); }
      }
      setSent(Math.round(((n + 1) / c.parts) * 100));
    }
    const fd = new FormData();
    fd.append("type", type.type);
    if (type.needs_plant) fd.append("plant", plant);
    return api.post(`/uploads/chunked/${c.token}/finish`, fd);
  };
  const send = async () => {
    setBusy(true);
    setErr(null);
    const fd = new FormData();
    fd.append("type", type.type);
    if (type.needs_plant) fd.append("plant", plant);
    fd.append("file", file);
    try {
      const r = type.streaming ? await chunked() : await api.post("/uploads", fd);
      toast.success(`${file.name} queued — processing in the background`);
      onStarted(r.data);
    } catch (e) {
      setErr({ msg: errMsg(e), missing: e.response?.data?.detail?.missing_columns || [] });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!type} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="upload-dialog">
        <DialogHeader><DialogTitle>Upload {type?.label}</DialogTitle></DialogHeader>
        <div
          data-testid={`upload-dropzone-${type?.type}`}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); setFile(e.dataTransfer.files[0]); setErr(null); }}
          onClick={() => input.current.click()}
          className={cn("flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors duration-150",
            drag ? "border-[#D52B3B] bg-[#D52B3B]/5" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50")}
        >
          <UploadCloud className="mb-2 h-8 w-8 text-slate-400" />
          {file ? (
            <p className="text-sm font-medium text-slate-800" data-testid="upload-selected-file">{file.name} · {(file.size / 1048576).toFixed(1)} MB</p>
          ) : (
            <p className="text-sm text-slate-600">Drop a {type?.streaming ? "ZIP, CSV or XLSX" : "CSV or XLSX"} here, or <span className="font-semibold text-[#D52B3B]">browse</span></p>
          )}
          <p className="mt-1 text-xs text-slate-400">Required columns: {type?.required.join(", ")}</p>
          {type?.streaming && <p className="mt-1 text-xs text-slate-500">Large files: ZIP/GZ containing the CSV (fastest, recommended), CSV or XLSX, up to 500 MB. Sent in 8 MB parts, imported in the background — you can leave this page.</p>}
          <input ref={input} type="file" accept={type?.streaming ? ".csv,.txt,.zip,.gz,.xlsx" : ".csv,.xlsx,.xlsm,.txt"} className="hidden" data-testid="upload-file-input"
            onChange={(e) => { setFile(e.target.files[0]); setErr(null); }} />
        </div>
        {type?.needs_plant && (
          <label className="block text-sm" data-testid="upload-plant-field">
            <span className="font-medium text-slate-700">Plant (the file has no plant column)</span>
            <select data-testid="upload-plant-select" value={plant} onChange={(e) => setPlant(e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3">
              <option value="">Choose plant…</option>
              {plants.map((p) => <option key={p.code} value={p.code}>Plant {p.code}{p.country ? ` · ${p.country}` : ""}</option>)}
            </select>
          </label>)}
        {err && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" data-testid="upload-error">
            <p className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" /> File rejected</p>
            <p className="mt-1">{err.msg}</p>
            {err.missing.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">{err.missing.map((m) => <span key={m} className="rounded bg-white px-1.5 py-0.5 font-mono text-xs">{m}</span>)}</div>
            )}
          </div>
        )}
        {busy && sent !== null && (
          <div data-testid="upload-transfer-progress"><p className="text-xs text-slate-600">Sending file… {sent}%</p><Progress value={sent} className="mt-1 h-2" /></div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="upload-submit-btn" disabled={!file || busy || (type?.needs_plant && !plant)} onClick={send}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Upload & import</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const eta = (s) => (s == null ? "" : s < 60 ? ` · ~${s}s left` : ` · ~${Math.round(s / 60)} min left`);

function ActiveJobs({ jobs, onDone }) {
  const [state, setState] = useState({});
  useEffect(() => {
    if (!jobs.length) return;
    const t = setInterval(async () => {
      for (const id of jobs) {
        const r = await api.get(`/uploads/${id}`);
        setState((s) => ({ ...s, [id]: r.data }));
        if (!["queued", "processing", "applying"].includes(r.data.status)) {
          onDone(id, r.data);
        }
      }
    }, 1200);
    return () => clearInterval(t);
  }, [jobs, onDone]);
  const cancel = async (id) => {
    try { await api.post(`/uploads/${id}/cancel`); toast.success("Cancelling after the current batch — rows already imported are kept"); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!jobs.length) return null;
  return (
    <div className="mb-6 space-y-3" data-testid="active-jobs">
      {jobs.map((id) => {
        const j = state[id];
        const total = j?.total_rows || j?.est_total_rows;
        return (
          <div key={id} className="rounded-xl border border-sky-200 bg-sky-50/60 p-4 fade-up" data-testid={`job-progress-${id}`}>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 font-medium text-slate-800"><Loader2 className="h-4 w-4 animate-spin text-sky-600" /> {j?.filename || "Starting…"} <span className="text-slate-500">· {j?.type_label}</span></span>
              <span className="flex items-center gap-3 text-xs text-slate-600" data-testid={`job-progress-text-${id}`}>
                {j?.stage || "queued"} · {fmtNum(j?.processed || 0)}{total ? ` / ${j?.total_rows ? "" : "~"}${fmtNum(total)}` : ""} rows{j?.rows_per_s ? ` · ${fmtNum(j.rows_per_s)}/s` : ""}{eta(j?.eta_s)}
                {j?.stalled && <span className="font-semibold text-amber-700" data-testid={`job-stalled-${id}`}>No progress for 10+ min</span>}
                {j?.cancel_requested ? <span className="text-rose-600">cancelling…</span> : <button className="rounded px-2 py-0.5 font-medium text-rose-700 hover:bg-rose-50" onClick={() => cancel(id)} data-testid={`job-cancel-${id}`}>Cancel</button>}
              </span>
            </div>
            <Progress value={j?.progress || 2} className="mt-3 h-2" />
          </div>
        );
      })}
    </div>
  );
}

function MarkFailedDialog({ rec, onClose, onDone }) {
  const [rm, setRm] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => setRm(false), [rec]);
  const go = async () => {
    setBusy(true);
    try { const r = await api.post(`/uploads/${rec.id}/mark-failed`, { remove_inserted: rm }); toast.success(`Marked failed${rm ? ` · ${fmtNum(r.data.removed_lines)} lines removed` : ""}`); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!rec} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="mark-failed-dialog">
        <DialogHeader><DialogTitle>Mark failed · {rec?.filename}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Status: {rec?.status} · {fmtNum(rec?.processed || 0)} rows processed, {fmtNum(rec?.inserted || 0)} inserted. Stops the import if it is still running.</p>
        <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={rm} onChange={(e) => setRm(e.target.checked)} data-testid="mark-failed-remove-checkbox" />
          <span>Also remove the sales lines this upload newly inserted (lines that existed before are kept). Leave unticked if you'll re-upload: a re-upload updates them without duplicates.</span></label>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" disabled={busy} onClick={go} data-testid="mark-failed-confirm-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Mark failed</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TypeCard({ t, last, canUpload, onUpload }) {
  return (
    <div className="group rounded-xl border border-slate-200 bg-white p-5 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-md" data-testid={`upload-type-card-${t.type}`}>
      <div className="flex items-start justify-between">
        <span className="rounded-lg bg-slate-100 p-2 text-slate-700"><FileSpreadsheet className="h-5 w-5" /></span>
        {last ? <StatusChip status={last.status} /> : <StatusChip status="queued" label="no data" />}
      </div>
      <h3 className="mt-4 font-semibold text-slate-900">{t.label}</h3>
      <p className="mt-0.5 text-xs text-slate-500">{t.columns.length} columns · key: {t.key.join(" + ")}</p>
      <p className="mt-3 text-xs text-slate-500">{last ? <>Last: {timeAgo(last.created_at)} · {fmtNum(last.total_rows)} rows · {last.source}</> : "Never uploaded"}</p>
      <div className="mt-4 flex gap-2">
        <Button size="sm" variant="outline" data-testid={`template-download-${t.type}`} onClick={() => download(`/uploads/templates/${t.type}`, `${t.type}_template.csv`)}>
          <Download className="mr-1.5 h-3.5 w-3.5" /> Template
        </Button>
        {canUpload && (
          <Button size="sm" data-testid={`upload-btn-${t.type}`} onClick={() => onUpload(t)}><UploadCloud className="mr-1.5 h-3.5 w-3.5" /> Upload</Button>
        )}
      </div>
    </div>
  );
}

export default function Uploads() {
  const { can } = useAuth();
  const [types, setTypes] = useState([]);
  const [last, setLast] = useState({});
  const [dlg, setDlg] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [reload, setReload] = useState(0);
  const [mf, setMf] = useState(null);
  const [pv, setPv] = useState(null);
  const resume = async (r) => {
    try { await api.post(`/uploads/${r.id}/resume`); toast.success("Resuming from the last saved batch"); setJobs((j) => [...new Set([...j, r.id])]); setReload((x) => x + 1); } catch (e) { toast.error(errMsg(e)); }
  };

  const loadLast = useCallback(async () => {
    const r = await api.get("/uploads", { params: { page_size: 100 } });
    const m = {};
    r.data.items.forEach((u) => { if (!m[u.type]) m[u.type] = u; });
    setLast(m);
    setJobs((j) => [...new Set([...j, ...r.data.items.filter((u) => ["queued", "processing", "applying"].includes(u.status)).map((u) => u.id)])]);
  }, []);
  useEffect(() => { api.get("/uploads/types").then((r) => setTypes(r.data)); loadLast(); }, [loadLast]);

  const onDone = useCallback((id, u) => {
    setJobs((j) => j.filter((x) => x !== id));
    setReload((x) => x + 1);
    loadLast();
    if (u.status === "preview_ready") { setPv(u); return; }
    if (u.type === "sap_codes" && u.applied) { toast.success(`${u.filename}: ${fmtNum(u.applied.codes_updated)} codes updated, ${fmtNum(u.applied.overrides_reset)} decisions reset, ${fmtNum(u.applied.sales_reclassified)} sales lines reclassified`); return; }
    const fn = ["failed", "cancelled"].includes(u.status) ? toast.error : u.failed ? toast.warning : toast.success;
    fn(`${u.filename}: ${u.status === "cancelled" ? "cancelled · " : ""}${fmtNum(u.inserted)} inserted, ${fmtNum(u.updated)} updated, ${fmtNum(u.failed)} failed${u.skipped_deleted ? ` · Skipped (deleted by user): ${fmtNum(u.skipped_deleted)}` : ""}${u.stock_replace ? ` · Plants replaced: ${u.stock_replace.plants.join(", ")} · set to zero: ${fmtNum(u.stock_replace.set_to_zero)}` : ""}${u.kept_corrections ? ` · Kept app code corrections: ${fmtNum(u.kept_corrections)}` : ""}${u.warnings?.length ? ` · ⚠ ${u.warnings.join("; ")}` : ""}${u.check_delivery_date ? ` · ${fmtNum(u.check_delivery_date)} lines "Check delivery date"` : ""}${u.lookup_summary ? ` · PO lines: ${fmtNum(u.lookup_summary.matched)} matched, ${fmtNum(u.lookup_summary.changed)} changed, ${fmtNum(u.lookup_summary.unmatched)} unmatched` : ""}${u.rmfg_summary ? ` · links: ${u.rmfg_summary.added} added, ${u.rmfg_summary.removed} removed, ${u.rmfg_summary.changed} changed, ${u.rmfg_summary.unchanged} unchanged` : ""}${u.rep_summary ? ` · account managers: ${u.rep_summary.assigned} assigned, ${u.rep_summary.moved} moved, ${u.rep_summary.unchanged} unchanged, ${u.rep_summary.unmapped} unmapped` : ""}`);
  }, [loadLast]);

  const columns = [
    { key: "created_at", label: "Time", render: (r) => fmtDate(r.created_at, true) },
    { key: "type_label", label: "Type", render: (r) => <span className="font-medium text-slate-800">{r.type_label}</span> },
    { key: "filename", label: "File", className: "max-w-[220px] truncate", render: (r) => <span title={r.filename}>{r.filename}</span> },
    { key: "source", label: "Source", render: (r) => <span className={cn("rounded px-1.5 py-0.5 text-xs", r.source === "manual" ? "bg-slate-100" : "bg-[#1C1C1E] text-white")}>{r.source}</span> },
    { key: "user", label: "User", render: (r) => <span className="text-xs">{r.user || "—"}</span> },
    { key: "status", label: "Status", render: (r) => <StatusChip status={r.status} testid={`history-status-${r.id}`} /> },
    { key: "total_rows", label: "Rows", className: "text-right", thClass: "text-right", render: (r) => fmtNum(r.total_rows || r.processed) },
    { key: "inserted", label: "Inserted", className: "text-right text-emerald-700", thClass: "text-right", render: (r) => <span data-testid={`history-inserted-${r.id}`}>{fmtNum(r.inserted)}</span> },
    { key: "updated", label: "Updated", className: "text-right text-sky-700", thClass: "text-right", render: (r) => <span data-testid={`history-updated-${r.id}`}>{fmtNum(r.updated)}</span> },
    { key: "failed", label: "Failed", className: "text-right", thClass: "text-right", render: (r) => <span className={r.failed ? "font-semibold text-rose-600" : ""} data-testid={`history-failed-${r.id}`}>{fmtNum(r.failed)}</span> },
    { key: "duration_s", label: "Time (s)", className: "text-right", thClass: "text-right", render: (r) => r.duration_s ?? "—" },
    {
      key: "actions", label: "", render: (r) => (
        <div className="flex items-center gap-1">
          {r.error && <span title={r.error} className="max-w-[220px] truncate text-xs text-rose-600" data-testid={`history-error-${r.id}`}>{r.error}</span>}
          {r.stalled && <span className="text-xs font-semibold text-amber-700" data-testid={`history-stalled-${r.id}`}>No progress for 10+ min</span>}
          {r.resumable && can.upload && <Button size="sm" variant="outline" className="h-7" onClick={() => resume(r)} data-testid={`history-resume-${r.id}`}>Resume from row {fmtNum(r.resume_from || 0)}</Button>}
          {can.admin && ["queued", "processing"].includes(r.status) && <Button size="sm" variant="ghost" className="h-7 text-rose-700" onClick={() => setMf(r)} data-testid={`history-mark-failed-${r.id}`}>Mark failed / clean up</Button>}
          {r.code_alert && <span className="text-xs text-slate-500" data-testid={`history-code-alert-${r.id}`}>{r.code_alert.skipped ? "Alerts off" : `Alert sent to ${r.code_alert.sent_to}`}</span>}
          {r.type === "sap_codes" && r.preview && can.override && <Button size="sm" variant="outline" className="h-7" onClick={() => setPv(r)} data-testid={`history-codes-preview-${r.id}`}>{r.status === "preview_ready" ? "Review & apply" : "Preview"}</Button>}
          {r.skipped_deleted > 0 && <span className="text-xs text-slate-500" data-testid={`history-skipped-deleted-${r.id}`}>Skipped (deleted by user): {fmtNum(r.skipped_deleted)}</span>}
          {r.stock_replace && (
            <button data-testid={`history-stock-replace-${r.id}`} title="Download previous vs this snapshot" onClick={() => download(`/uploads/${r.id}/stock-compare`, `${r.filename}_stock_compare.csv`)} className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-slate-100">
              Plants replaced: {r.stock_replace.plants.join(", ")} · set to zero: {fmtNum(r.stock_replace.set_to_zero)}
            </button>
          )}
          {r.kept_corrections > 0 && <span className="text-xs text-amber-700" data-testid={`history-kept-codes-${r.id}`}>Kept app code corrections: {fmtNum(r.kept_corrections)}</span>}
          {r.failed > 0 && (
            <button data-testid={`failed-rows-download-${r.id}`} onClick={() => download(`/uploads/${r.id}/failed-rows`, `${r.filename}_failed_rows.csv`)} className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50">
              <FileDown className="h-3.5 w-3.5" /> Failed rows
            </button>
          )}
          {r.storage_path && (
            <button data-testid={`original-download-${r.id}`} onClick={() => download(`/uploads/${r.id}/original`, r.filename)} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Download original">
              <Download className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="uploads-page">
      <PageHeader title="Upload management" subtitle="Import the six SAP extracts. Files are validated, cleaned (HTML, £, thousand separators, DD-MM-YYYY), upserted on natural keys and stored in object storage." />
      {!can.upload && (
        <div className="mb-5 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600" data-testid="viewer-readonly-note">
          <X className="h-4 w-4" /> Read-only access: uploading is disabled for your role.
        </div>
      )}
      <ActiveJobs jobs={jobs} onDone={onDone} />
      <div className="stagger grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {types.map((t) => <TypeCard key={t.type} t={t} last={last[t.type]} canUpload={t.roles ? can.override : can.upload} onUpload={setDlg} />)}
      </div>
      <div className="mb-3 mt-10 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Upload history</h2>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="h-9 w-48 bg-white" data-testid="history-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {types.map((t) => <SelectItem key={t.type} value={t.type}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <DataTable endpoint="/uploads" params={{ type: filter }} columns={columns} testid="upload-history-table" pageSize={25} reloadKey={reload} />
      <UploadDialog type={dlg} onClose={() => setDlg(null)} onStarted={(u) => { setDlg(null); setJobs((j) => [...j, u.id]); setReload((x) => x + 1); }} />
      <SapCodesPreview rec={pv} onClose={() => setPv(null)} onApplied={(id) => { setPv(null); setJobs((j) => [...new Set([...j, id])]); setReload((x) => x + 1); }} />
      <MarkFailedDialog rec={mf} onClose={() => setMf(null)} onDone={() => { setMf(null); setReload((x) => x + 1); loadLast(); }} />
    </div>
  );
}
